/**
 * MeCare — Phase 1 care trackers:
 *  • VitalsTracker      /me/health/vitals   (BP · Sugar · Oxygen · Weight · Temp · Water)
 *  • BabyTracker        /me/health/baby     (Feed · Sleep · Diaper)
 *  • EmergencyContacts  /me/health/emergency (Ambulance · Doctor · Hospital · …)
 *
 * All screens share the "loved-one picker" chip strip from MeHealth.
 * Emergency uses "tel:" links for one-touch dial on mobile.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { custApi, formatErr } from "../../lib/customerApi";
import {
  ArrowLeft, Plus, Trash, Users, Heart, Drop, ThermometerSimple,
  Pulse, Baby, Bed, Phone, Ambulance, Stethoscope,
  Hospital, Skull, PawPrint, UsersThree,
  ArrowRight, Clock, Scales,
} from "@phosphor-icons/react";

// ══════════════════════════════════════════════════════════════════
// Utility helpers
// ══════════════════════════════════════════════════════════════════
function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - then) / 60000);
  if (diff < 1) return "just now";
  if (diff < 60) return `${diff}m ago`;
  const h = Math.floor(diff / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

// ══════════════════════════════════════════════════════════════════
// Shared BackHeader (mirror of MeHealth)
// ══════════════════════════════════════════════════════════════════
function BackHeader({ to, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <Link to={to} className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shrink-0 hover:bg-gray-50">
        <ArrowLeft size={16} weight="bold" className="text-gray-700"/>
      </Link>
      <div className="flex-1 min-w-0">
        <h1 className="font-display font-black text-[22px] leading-tight tracking-tight text-[#111827]">
          {title}
        </h1>
        {subtitle && <div className="text-[12px] text-gray-500 mt-0.5">{subtitle}</div>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Loved-one picker (chip strip). If empty, prompts to Profile.
// ══════════════════════════════════════════════════════════════════
function LovedOnePicker({ items, value, onChange, filterKind }) {
  const pool = filterKind ? items.filter(i => (i.kind || "self") === filterKind) : items;
  if (items.length === 0) {
    return (
      <div className="border border-dashed border-[#DC2626] bg-[#FEF7F6] rounded-md p-4 text-center"
           data-testid="lo-empty">
        <Users size={22} color="#DC2626" className="mx-auto mb-1"/>
        <div className="text-sm font-semibold text-[#1A1A1A]">Add a loved one first</div>
        <div className="text-[11px] text-[#5C5A55] mt-1">
          Head to Profile → Health Profile to add family members.
        </div>
        <Link to="/me/profile" data-testid="lo-goto-profile"
              className="mt-3 inline-block h-9 px-4 leading-9 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-medium">
          Go to Profile
        </Link>
      </div>
    );
  }
  if (pool.length === 0 && filterKind) {
    return (
      <div className="border border-dashed border-[#DC2626] bg-[#FEF7F6] rounded-md p-4 text-center">
        <div className="text-[12px] text-[#5C5A55]">
          No one tagged as <b>{filterKind}</b> yet. Update someone&apos;s Care Kind in Profile → Health Profile.
        </div>
      </div>
    );
  }
  return (
    <div data-testid="lo-picker">
      <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1.5 font-semibold">
        For whom?
      </label>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
        {pool.map(lo => {
          const on = lo.id === value;
          return (
            <button key={lo.id}
                    data-testid={`lo-chip-${lo.id}`}
                    onClick={() => onChange(lo.id)}
                    className={`h-10 px-3 shrink-0 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                      on ? "bg-[#DC2626] text-white border-[#DC2626]"
                         : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                    }`}>
              <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${
                on ? "bg-white/25" : "bg-[#FEE2E2] text-[#DC2626]"
              }`}>{lo.name?.[0]?.toUpperCase() || "?"}</span>
              {lo.name}
              <span className={`text-[9px] uppercase tracking-widest ${on ? "opacity-90" : "opacity-60"}`}>
                {lo.kind || lo.relation}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Vitals Tracker · /me/health/vitals
// ══════════════════════════════════════════════════════════════════
const VITAL_META = {
  bp:     { Icon: Heart,             color: "#DC2626", label: "Blood Pressure", unit: "mmHg", bg: "#FEE2E2" },
  sugar:  { Icon: Drop,              color: "#B45309", label: "Blood Sugar",    unit: "mg/dL", bg: "#FEF3C7" },
  oxygen: { Icon: Pulse,             color: "#0F766E", label: "Oxygen (SpO2)",  unit: "%",     bg: "#CCFBF1" },
  weight: { Icon: Scales,            color: "#7C3AED", label: "Weight",         unit: "kg",    bg: "#EDE9FE" },
  temp:   { Icon: ThermometerSimple, color: "#EA580C", label: "Temperature",    unit: "°F",    bg: "#FFEDD5" },
  water:  { Icon: Drop,              color: "#2563EB", label: "Water",          unit: "ml",    bg: "#DBEAFE" },
};

export function VitalsTracker() {
  const [lovedOnes, setLovedOnes] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState("bp");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ systolic: "", diastolic: "", value: "", notes: "" });

  const loadLO = async () => {
    try {
      const r = await custApi.get("/me/loved-ones");
      const list = r.data.loved_ones || [];
      setLovedOnes(list);
      if (list.length && !selectedId) setSelectedId(list[0].id);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const load = async () => {
    if (!selectedId) return;
    try {
      const r = await custApi.get("/me/vitals", { params: { loved_one_id: selectedId } });
      setRows(r.data.vitals || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { loadLO(); }, []);
  useEffect(() => { load(); }, [selectedId]);

  const save = async () => {
    const payload = {
      loved_one_id: selectedId,
      kind,
      unit: VITAL_META[kind].unit,
      notes: form.notes.trim() || null,
    };
    if (kind === "bp") {
      const sys = Number(form.systolic), dia = Number(form.diastolic);
      if (!sys || !dia) return toast.error("Enter systolic and diastolic");
      payload.value_systolic = sys;
      payload.value_diastolic = dia;
    } else {
      const v = Number(form.value);
      if (!v) return toast.error("Enter a value");
      payload.value = v;
    }
    try {
      await custApi.post("/me/vitals", payload);
      toast.success("Logged");
      setForm({ systolic: "", diastolic: "", value: "", notes: "" });
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await custApi.delete(`/me/vitals/${id}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const filtered = rows.filter(r => r.kind === kind);
  const meta = VITAL_META[kind];

  return (
    <div className="space-y-4 px-4 py-4" data-testid="me-vitals">
      <BackHeader to="/me/health" title="Vitals log"
                    subtitle="BP · Sugar · Oxygen · Weight · Temp · Water"/>
      <LovedOnePicker items={lovedOnes} value={selectedId} onChange={setSelectedId}/>

      {selectedId && (
        <>
          {/* Vital kind selector */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4" data-testid="vitals-kind-tabs">
            {Object.keys(VITAL_META).map(k => {
              const on = k === kind;
              const m = VITAL_META[k];
              return (
                <button key={k}
                        data-testid={`vitals-tab-${k}`}
                        onClick={() => setKind(k)}
                        className={`h-9 px-3 shrink-0 rounded-full inline-flex items-center gap-1.5 text-xs font-semibold transition-colors ${
                          on ? "text-white" : "text-[#374151] bg-white border border-gray-200"
                        }`}
                        style={on ? { backgroundColor: m.color } : {}}>
                  <m.Icon size={13} weight="fill"/> {m.label.split(" ")[0]}
                </button>
              );
            })}
          </div>

          {/* Latest reading pill */}
          {filtered[0] && (
            <div className="rounded-2xl p-4 border" style={{ backgroundColor: meta.bg, borderColor: meta.color + "22" }}>
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: meta.color }}>
                Latest {meta.label}
              </div>
              <div className="font-display font-black text-2xl mt-1" style={{ color: meta.color }}>
                {kind === "bp"
                  ? `${filtered[0].value_systolic || "?"}/${filtered[0].value_diastolic || "?"} ${meta.unit}`
                  : `${filtered[0].value ?? "—"} ${meta.unit}`}
              </div>
              <div className="text-[11px] text-gray-600 mt-1">{timeAgo(filtered[0].ts)}</div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-[#5C5A55] font-semibold">
              History ({filtered.length})
            </div>
            {!showForm && (
              <button onClick={() => setShowForm(true)}
                      data-testid="vitals-add"
                      className="h-9 px-3 inline-flex items-center gap-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-medium">
                <Plus size={12} weight="bold"/> Log
              </button>
            )}
          </div>

          {showForm && (
            <div className="border-2 border-dashed border-[#DC2626] rounded-md p-3 bg-[#FEF7F6] space-y-2"
                 data-testid="vitals-form">
              {kind === "bp" ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Systolic</label>
                    <input type="number" value={form.systolic}
                           onChange={e => setForm(s => ({ ...s, systolic: e.target.value }))}
                           data-testid="vitals-systolic"
                           placeholder="120"
                           className="w-full h-10 px-2 border border-[#E5E2D9] rounded text-sm font-mono"/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Diastolic</label>
                    <input type="number" value={form.diastolic}
                           onChange={e => setForm(s => ({ ...s, diastolic: e.target.value }))}
                           data-testid="vitals-diastolic"
                           placeholder="80"
                           className="w-full h-10 px-2 border border-[#E5E2D9] rounded text-sm font-mono"/>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">
                    Value ({meta.unit})
                  </label>
                  <input type="number" step="any" value={form.value}
                         onChange={e => setForm(s => ({ ...s, value: e.target.value }))}
                         data-testid="vitals-value"
                         placeholder={kind === "sugar" ? "110" : kind === "oxygen" ? "98" : ""}
                         className="w-full h-10 px-2 border border-[#E5E2D9] rounded text-sm font-mono"/>
                </div>
              )}
              <input value={form.notes}
                     onChange={e => setForm(s => ({ ...s, notes: e.target.value }))}
                     data-testid="vitals-notes"
                     placeholder="Notes (fasting, after food…)"
                     className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button onClick={save} data-testid="vitals-save"
                        className="h-10 w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-medium">
                  Save entry
                </button>
                <button onClick={() => setShowForm(false)}
                        className="h-10 w-full border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
              </div>
            </div>
          )}

          {filtered.length === 0 && !showForm ? (
            <div className="border border-dashed border-[#E5E2D9] rounded-md p-6 text-center">
              <meta.Icon size={26} color="#8A8780" className="mx-auto mb-1.5"/>
              <div className="text-xs text-[#5C5A55]">No {meta.label.toLowerCase()} readings yet. Tap Log.</div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="vitals-list">
              {filtered.map(r => (
                <div key={r.id} data-testid={`vitals-row-${r.id}`}
                     className="border border-[#E5E2D9] rounded-md p-3 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0"
                       style={{ backgroundColor: meta.bg, color: meta.color }}>
                    <meta.Icon size={16} weight="fill"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[#1A1A1A]">
                      {kind === "bp"
                        ? `${r.value_systolic}/${r.value_diastolic} ${meta.unit}`
                        : `${r.value} ${meta.unit}`}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      <Clock size={9} className="inline mr-0.5"/> {fmtTime(r.ts)} · {timeAgo(r.ts)}
                    </div>
                    {r.notes && <div className="text-[11px] text-[#5C5A55] mt-0.5">{r.notes}</div>}
                  </div>
                  <button onClick={() => remove(r.id)} data-testid={`vitals-del-${r.id}`}
                          className="p-1.5 rounded hover:bg-[#FEE2E2]">
                    <Trash size={13} color="#DC2626"/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Baby Tracker · /me/health/baby
// ══════════════════════════════════════════════════════════════════
const BABY_META = {
  feed:   { Icon: Baby, color: "#2563EB", label: "Feed",   bg: "#DBEAFE" },
  sleep:  { Icon: Bed,  color: "#7C3AED", label: "Sleep",  bg: "#EDE9FE" },
  diaper: { Icon: Drop, color: "#EA580C", label: "Diaper", bg: "#FFEDD5" },
};

export function BabyTracker() {
  const [lovedOnes, setLovedOnes] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(null); // null | 'feed' | 'sleep' | 'diaper'
  const [form, setForm] = useState({ amount_ml: "", duration_min: "", diaper_type: "wet", notes: "" });

  const loadLO = async () => {
    try {
      const r = await custApi.get("/me/loved-ones");
      const list = r.data.loved_ones || [];
      setLovedOnes(list);
      const baby = list.find(l => l.kind === "baby") || list[0];
      if (baby && !selectedId) setSelectedId(baby.id);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const load = async () => {
    if (!selectedId) return;
    try {
      const r = await custApi.get("/me/baby-events", { params: { loved_one_id: selectedId, limit: 50 } });
      setRows(r.data.events || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { loadLO(); }, []);
  useEffect(() => { load(); }, [selectedId]);

  const quickLog = async (kind, extra = {}) => {
    try {
      await custApi.post("/me/baby-events", { loved_one_id: selectedId, kind, ...extra });
      toast.success(`${BABY_META[kind].label} logged`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const saveDetailed = async () => {
    const k = showForm;
    const payload = { loved_one_id: selectedId, kind: k, notes: form.notes.trim() || null };
    if (k === "feed" && form.amount_ml) payload.amount_ml = Number(form.amount_ml);
    if ((k === "feed" || k === "sleep") && form.duration_min) payload.duration_min = Number(form.duration_min);
    if (k === "diaper") payload.diaper_type = form.diaper_type;
    try {
      await custApi.post("/me/baby-events", payload);
      toast.success(`${BABY_META[k].label} logged`);
      setForm({ amount_ml: "", duration_min: "", diaper_type: "wet", notes: "" });
      setShowForm(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await custApi.delete(`/me/baby-events/${id}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Today's tally
  const today = new Date().toISOString().slice(0, 10);
  const tally = rows.reduce((acc, r) => {
    if ((r.ts || "").slice(0, 10) === today) acc[r.kind] = (acc[r.kind] || 0) + 1;
    return acc;
  }, {});
  const lastByKind = useMemo(() => {
    const out = {};
    for (const r of rows) if (!out[r.kind]) out[r.kind] = r;
    return out;
  }, [rows]);

  return (
    <div className="space-y-4 px-4 py-4" data-testid="me-baby">
      <BackHeader to="/me/health" title="Baby tracker"
                    subtitle="Feed · Sleep · Diaper — tap once to log."/>
      <LovedOnePicker items={lovedOnes} value={selectedId} onChange={setSelectedId}/>

      {selectedId && (
        <>
          {/* Today's stat cards */}
          <div className="grid grid-cols-3 gap-2" data-testid="baby-today-stats">
            {["feed", "sleep", "diaper"].map(k => {
              const m = BABY_META[k];
              return (
                <div key={k}
                     className="rounded-2xl p-3 border"
                     style={{ backgroundColor: m.bg, borderColor: m.color + "22" }}>
                  <m.Icon size={16} weight="fill" color={m.color}/>
                  <div className="font-display font-black text-xl mt-1" style={{ color: m.color }}>
                    {tally[k] || 0}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: m.color }}>
                    {m.label} today
                  </div>
                  {lastByKind[k] && (
                    <div className="text-[10px] text-gray-600 mt-0.5">
                      Last: {timeAgo(lastByKind[k].ts)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* One-tap log buttons */}
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => quickLog("feed")}
                    data-testid="baby-quick-feed"
                    className="h-11 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5">
              <Baby size={13}/> +Feed
            </button>
            <button onClick={() => quickLog("sleep")}
                    data-testid="baby-quick-sleep"
                    className="h-11 rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5">
              <Bed size={13}/> +Sleep
            </button>
            <button onClick={() => quickLog("diaper", { diaper_type: "wet" })}
                    data-testid="baby-quick-diaper"
                    className="h-11 rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5">
              <Drop size={13}/> +Diaper
            </button>
          </div>

          {/* Add detail toggle */}
          <div className="flex justify-center">
            {!showForm ? (
              <button onClick={() => setShowForm("feed")}
                      data-testid="baby-detail-toggle"
                      className="text-[11px] font-semibold text-[#DC2626] hover:underline">
                Add with details →
              </button>
            ) : (
              <div className="w-full border-2 border-dashed border-[#DC2626] rounded-md p-3 bg-[#FEF7F6] space-y-2">
                <div className="flex gap-1.5">
                  {["feed", "sleep", "diaper"].map(k => (
                    <button key={k} onClick={() => setShowForm(k)}
                            className={`flex-1 h-9 text-xs font-semibold rounded ${
                              showForm === k ? "bg-[#DC2626] text-white" : "bg-white border border-[#E5E2D9]"
                            }`}>
                      {BABY_META[k].label}
                    </button>
                  ))}
                </div>
                {showForm === "feed" && (
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" value={form.amount_ml}
                           onChange={e => setForm(s => ({ ...s, amount_ml: e.target.value }))}
                           placeholder="Amount (ml)"
                           className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
                    <input type="number" value={form.duration_min}
                           onChange={e => setForm(s => ({ ...s, duration_min: e.target.value }))}
                           placeholder="Duration (min)"
                           className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
                  </div>
                )}
                {showForm === "sleep" && (
                  <input type="number" value={form.duration_min}
                         onChange={e => setForm(s => ({ ...s, duration_min: e.target.value }))}
                         placeholder="Duration (min)"
                         className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
                )}
                {showForm === "diaper" && (
                  <div className="grid grid-cols-3 gap-1.5">
                    {["wet", "soiled", "both"].map(t => (
                      <button key={t} onClick={() => setForm(s => ({ ...s, diaper_type: t }))}
                              className={`h-9 text-xs font-semibold rounded ${
                                form.diaper_type === t ? "bg-[#EA580C] text-white" : "bg-white border border-[#E5E2D9]"
                              }`}>
                        {t}
                      </button>
                    ))}
                  </div>
                )}
                <input value={form.notes}
                       onChange={e => setForm(s => ({ ...s, notes: e.target.value }))}
                       placeholder="Notes"
                       className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button onClick={saveDetailed}
                          className="h-9 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-medium">
                    Save
                  </button>
                  <button onClick={() => setShowForm(null)}
                          className="h-9 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* Recent log */}
          <div className="text-[11px] uppercase tracking-widest text-[#5C5A55] font-semibold">
            Recent activity ({rows.length})
          </div>
          {rows.length === 0 ? (
            <div className="border border-dashed border-[#E5E2D9] rounded-md p-6 text-center">
              <Baby size={26} color="#8A8780" className="mx-auto mb-1.5"/>
              <div className="text-xs text-[#5C5A55]">No entries yet.</div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="baby-list">
              {rows.map(r => {
                const m = BABY_META[r.kind] || BABY_META.feed;
                return (
                  <div key={r.id} data-testid={`baby-row-${r.id}`}
                       className="border border-[#E5E2D9] rounded-md p-3 flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0"
                         style={{ backgroundColor: m.bg, color: m.color }}>
                      <m.Icon size={15} weight="fill"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-[#1A1A1A]">
                        {m.label}
                        {r.amount_ml ? <span className="ml-1.5 text-[10px] text-gray-500">{r.amount_ml} ml</span> : null}
                        {r.duration_min ? <span className="ml-1.5 text-[10px] text-gray-500">{r.duration_min} min</span> : null}
                        {r.diaper_type ? <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-[#FFEDD5] text-[#7A2E18] font-medium">{r.diaper_type}</span> : null}
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        <Clock size={9} className="inline mr-0.5"/> {fmtTime(r.ts)} · {timeAgo(r.ts)}
                      </div>
                      {r.notes && <div className="text-[11px] text-[#5C5A55] mt-0.5">{r.notes}</div>}
                    </div>
                    <button onClick={() => remove(r.id)} data-testid={`baby-del-${r.id}`}
                            className="p-1.5 rounded hover:bg-[#FEE2E2]">
                      <Trash size={13} color="#DC2626"/>
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Diaper → pickup hook (Rescigo unique value) */}
          {(tally.diaper || 0) >= 3 && (
            <Link to="/me/requests?new=1" data-testid="baby-pickup-cta"
                  className="block rounded-2xl p-4 bg-gradient-to-br from-[#DC2626] via-[#B91C1C] to-[#7F1D1D] text-white shadow-[0_12px_25px_rgba(220,38,38,0.30)]">
              <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">Rescigo tip</div>
              <div className="font-display font-black text-[16px] leading-tight mt-1">
                Baby went through {tally.diaper} diapers today.
              </div>
              <div className="text-[12px] opacity-90 mt-1">Book a biohazard pickup so waste doesn&apos;t pile up →</div>
            </Link>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Emergency Contacts · /me/health/emergency
// ══════════════════════════════════════════════════════════════════
const EMERGENCY_META = {
  ambulance: { Icon: Ambulance,    color: "#DC2626", label: "Ambulance",   bg: "#FEE2E2", hint: "108 (Kerala)" },
  doctor:    { Icon: Stethoscope,  color: "#2563EB", label: "Doctor",      bg: "#DBEAFE", hint: "Family GP" },
  hospital:  { Icon: Hospital,     color: "#7C3AED", label: "Hospital",    bg: "#EDE9FE", hint: "Nearest ER" },
  poison:    { Icon: Skull,        color: "#B45309", label: "Poison line", bg: "#FEF3C7", hint: "1066 (India)" },
  animal:    { Icon: PawPrint,     color: "#0F766E", label: "Animal help", bg: "#CCFBF1", hint: "Vet emergency" },
  family:    { Icon: UsersThree,   color: "#EA580C", label: "Family",      bg: "#FFEDD5", hint: "Trusted number" },
};

const DEFAULT_EMERGENCIES = [
  { label: "ambulance", name: "108 Kerala Ambulance", phone: "108", is_primary: true, order: 1 },
  { label: "poison",    name: "National Poison Line", phone: "1066", is_primary: true, order: 2 },
];

export function EmergencyContacts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: "ambulance", name: "", phone: "", notes: "", is_primary: false });

  const load = async () => {
    try {
      const r = await custApi.get("/me/emergency-contacts");
      setRows(r.data.contacts || []);
    } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const seedDefaults = async () => {
    try {
      for (const c of DEFAULT_EMERGENCIES) {
        await custApi.post("/me/emergency-contacts", c);
      }
      toast.success("Default contacts added");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Name & phone required");
    try {
      await custApi.post("/me/emergency-contacts", { ...form, order: rows.length + 1 });
      toast.success("Contact added");
      setForm({ label: "ambulance", name: "", phone: "", notes: "", is_primary: false });
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this contact?")) return;
    try {
      await custApi.delete(`/me/emergency-contacts/${id}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4 px-4 sm:px-6 py-5" data-testid="me-emergency">
      <BackHeader to="/me/health" title="Emergency"
                    subtitle="One-touch call · saved contacts"/>

      {/* Big SOS card */}
      <a href="tel:108" data-testid="emg-sos-108"
         className="block rounded-3xl p-5 bg-gradient-to-br from-[#DC2626] via-[#B91C1C] to-[#7F1D1D] text-white shadow-[0_20px_50px_rgba(220,38,38,0.30)]">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur inline-flex items-center justify-center">
            <Ambulance size={26} weight="fill"/>
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">Tap to call</div>
            <div className="font-display font-black text-[24px] leading-tight">Ambulance · 108</div>
            <div className="text-[12px] opacity-90 mt-0.5">Free ambulance dispatch anywhere in Kerala</div>
          </div>
          <Phone size={20} weight="fill" className="opacity-80"/>
        </div>
      </a>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-[#5C5A55] font-semibold">
          Your contacts ({rows.length})
        </div>
        <div className="flex gap-1.5">
          {rows.length === 0 && !loading && (
            <button onClick={seedDefaults} data-testid="emg-seed"
                    className="h-9 px-3 border border-[#DC2626] text-[#DC2626] rounded-md text-xs font-medium">
              Seed defaults
            </button>
          )}
          {!showForm && (
            <button onClick={() => setShowForm(true)} data-testid="emg-add"
                    className="h-9 px-3 inline-flex items-center gap-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-medium">
              <Plus size={12} weight="bold"/> Add
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="border-2 border-dashed border-[#DC2626] rounded-md p-3 bg-[#FEF7F6] space-y-2" data-testid="emg-form">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Type</label>
            <select value={form.label}
                    onChange={e => setForm(s => ({ ...s, label: e.target.value }))}
                    data-testid="emg-label"
                    className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm bg-white">
              {Object.keys(EMERGENCY_META).map(k => (
                <option key={k} value={k}>{EMERGENCY_META[k].label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={form.name}
                   onChange={e => setForm(s => ({ ...s, name: e.target.value }))}
                   data-testid="emg-name"
                   placeholder="Name (e.g. Dr. Ravi)"
                   className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            <input value={form.phone}
                   onChange={e => setForm(s => ({ ...s, phone: e.target.value }))}
                   data-testid="emg-phone"
                   placeholder="Phone"
                   className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm font-mono"/>
          </div>
          <input value={form.notes}
                 onChange={e => setForm(s => ({ ...s, notes: e.target.value }))}
                 placeholder="Notes (optional)"
                 className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.is_primary}
                   onChange={e => setForm(s => ({ ...s, is_primary: e.target.checked }))}/>
            Mark as primary (show first)
          </label>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button onClick={save} data-testid="emg-save"
                    className="h-9 w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-medium">
              Save
            </button>
            <button onClick={() => setShowForm(false)}
                    className="h-9 w-full border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-xs text-[#8A8780] text-center py-6">Loading…</div>
      ) : rows.length === 0 && !showForm ? (
        <div className="border border-dashed border-[#E5E2D9] rounded-md p-6 text-center">
          <Ambulance size={26} color="#8A8780" className="mx-auto mb-1.5"/>
          <div className="text-xs text-[#5C5A55]">No contacts saved. Tap <b>Seed defaults</b> to add 108 &amp; Poison line.</div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="emg-list">
          {rows.map(row => {
            const m = EMERGENCY_META[row.label] || EMERGENCY_META.family;
            return (
              <div key={row.id} data-testid={`emg-row-${row.id}`}
                   className="border border-[#E5E2D9] rounded-md p-3 flex items-center gap-3 hover:border-[#DC2626] transition-colors">
                <div className="w-11 h-11 rounded-full inline-flex items-center justify-center shrink-0"
                     style={{ backgroundColor: m.bg, color: m.color }}>
                  <m.Icon size={18} weight="fill"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[#1A1A1A] truncate">
                    {row.name}
                    {row.is_primary && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#DC2626] font-bold">PRIMARY</span>}
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {m.label} · <span className="font-mono">{row.phone}</span>
                  </div>
                </div>
                <a href={`tel:${row.phone}`}
                   data-testid={`emg-call-${row.id}`}
                   className="w-10 h-10 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white inline-flex items-center justify-center shrink-0">
                  <Phone size={14} weight="fill"/>
                </a>
                <button onClick={() => remove(row.id)}
                        data-testid={`emg-del-${row.id}`}
                        className="p-1.5 rounded hover:bg-[#FEE2E2]">
                  <Trash size={13} color="#DC2626"/>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
